// BookingProcess.js
import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Stack
} from '@mui/material';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const BookingProcess = () => {
  const ORANGE = '#ff7f00';
  const GREEN = '#43934A';

  const steps = [
    {
      title: 'Book Consultations',
      desc: 'Tell us what type of  service you need, and preferred date and time.',
      Icon: CalendarMonthIcon
    },
    {
      title: 'Choose Package',
      desc: 'We’ll provide a price and time estimate for the service as well as available time slots that match your schedule.',
      Icon: DoneAllIcon
    },
    {
      title: 'We Care, You Relax',
      desc: 'Our professional team will arrive on time with all supplies and perform a detailed service as estimate agreed.',
      Icon: AutoAwesomeIcon
    }
  ];

  const StepCard = ({ Icon, title, desc }) => (
    <Box sx={{ textAlign: 'center', position: 'relative' }}>
      {/* Circle icon */}
      <Box
        sx={{
          width: 120,
          height: 120,
          mx: 'auto',
          borderRadius: '50%',
          bgcolor: 'rgba(67,147,74,0.25)', // outer ring
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Box
          sx={{
            width: 98,
            height: 98,
            borderRadius: '50%',
            bgcolor: GREEN,
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 42 }} />
        </Box>
      </Box>

      <Typography
        variant="h5"
        sx={{
          mt: 3,
          fontWeight: 800,
          letterSpacing: '-0.02em'
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mt: 2,
          color: 'text.secondary',
          maxWidth: 360,
          mx: 'auto',
          lineHeight: 1.7
        }}
      >
        {desc}
      </Typography>
    </Box>
  );

  // Curved arrow (SVG) used between steps on desktop
  const CurvedArrow = ({ flip = false }) => (
    <Box
      sx={{
        position: 'absolute',
        top: 118,
        left: 'calc(100% - 18px)',
        width: 120,
        height: 90,
        display: { xs: 'none', md: 'block' },
        pointerEvents: 'none',
        transform: flip ? 'scaleY(-1)' : 'none'
      }}
      aria-hidden="true"
    >
      <svg width="120" height="90" viewBox="0 0 120 90" fill="none">
        <path
          d="M14 18 C 44 0, 78 0, 96 26"
          stroke={ORANGE}
          strokeWidth="3.5"
          strokeLinecap="round"
        />
        <path
          d="M92 22 L100 28 L92 34"
          stroke={ORANGE}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );

  return (
    <Box
      id="booking-process"
      sx={{
        py: { xs: 8, md: 10 },
        bgcolor: '#fff'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Caveat", cursive',
              color: ORANGE,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 700,
              mb: 1
            }}
          >
            Book in 60 seconds
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              fontSize: { xs: 38, md: 56 },
              lineHeight: 1.05
            }}
          >
            Quick &amp; Easy Booking Process
          </Typography>

          <Box sx={{ height: 24 }} />
        </Box>

        {/* Steps */}
        <Grid
          container
          spacing={{ xs: 6, md: 8 }}
          sx={{
            mt: { xs: 2, md: 3 },
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}
        >
          {steps.map((s, idx) => (
            <Grid key={s.title} item xs={12} md={4} sx={{ position: 'relative' }}>
              <StepCard Icon={s.Icon} title={s.title} desc={s.desc} />
              {/* arrows between 1->2 (top curve), 2->3 (bottom curve like screenshot) */}
              {idx === 0 && <CurvedArrow />}
              {idx === 1 && <CurvedArrow flip />}
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Stack alignItems="center" sx={{ mt: { xs: 6, md: 7 } }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: ORANGE,
              px: 4.5,
              py: 1.4,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: 18,
              boxShadow: '0 10px 24px rgba(255,127,0,0.25)',
              '&:hover': { bgcolor: '#ff6f00' }
            }}
          >
            Get Started
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default BookingProcess;
